class FaqSection extends HTMLElement {
    constructor() {
        super();
        this.onNavClick = this.onNavClick.bind(this);
        this.onSearchSubmit = this.onSearchSubmit.bind(this);
        this.onSearchKeydown = this.onSearchKeydown.bind(this);
        this.filterAndRender = this.filterAndRender.bind(this);
        this.submittedQuery = '';
        this.unicodePropsSupported = (() => {
            try {
                void new RegExp('\\p{L}', 'u');
                return true;
            } catch (e) {
                return false;
            }
        })();
    }

    connectedCallback() {
        this.initDomRefs();
        this.readConfig();
        this.buildIndex();
        this.initViewMoreButtons();
        this.bindEvents();
        this.filterAndRender();
        this._observer = new MutationObserver((mutations) => this.onMutations(mutations));
        this._observer.observe(this, {
            subtree: true,
            childList: true
        });
    }

    onMutations(mutations) {
        const isRelevant = (node) => {
            if (!node || node.nodeType !== 1) return false;
            if (node.matches && node.matches('[data-faq-group],[data-faq-item]')) return true;
            return !!(node.querySelector && node.querySelector('[data-faq-group],[data-faq-item]'));
        };
        const isRelevantMutation = (m) =>
            Array.from(m.addedNodes).some(isRelevant) || Array.from(m.removedNodes).some(isRelevant);

        if (mutations.some(isRelevantMutation)) this.reindexDeferred();
    }

    disconnectedCallback() {
        this.unbindEvents();
        if (this._observer) this._observer.disconnect();
        clearTimeout(this._reindexDebounceId);
    }

    initDomRefs() {
        this.groupsContainer = this.querySelector('.faq-groups');
        this.navContainer = this.querySelector('.faq-nav');
        this.navButtons = Array.from(this.querySelectorAll('.faq-nav-btn'));
        this.searchInput = this.querySelector('input[type="search"]');
        this.searchSubmitBtn = this.querySelector('.faq-search [data-submit-search]') || this.querySelector('.faq-search button');
        this.noResultEl = this.querySelector('.faq-no-result');
        this.countEl = this.querySelector('[data-faq-count]');

        this.categoryGroups = Array.from(this.querySelectorAll('[data-faq-group]'));
        this.faqItems = Array.from(this.querySelectorAll('[data-faq-item]'));
    }

    readConfig() {
        const groupsRootEl = this.groupsContainer;
        this.searchMode = groupsRootEl?.getAttribute('data-search-mode') || 'smart'; // smart | phrase | all | any
        this.searchScope = groupsRootEl?.getAttribute('data-search-scope') || 'only_active'; // only_active | always_all
        this.initialLimit = parseInt(groupsRootEl?.getAttribute('data-initial-limit'), 10) || 3;
    }

    normalize(s) {
        return (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
    collapseSpaces(s) {
        return this.normalize(s).replace(/\s+/g, ' ').trim();
    }
    tokenize(input) {
        const normalized = this.normalize(input);
        const wordPattern = this.unicodePropsSupported ?
            /\p{L}[\p{L}\p{M}\p{N}'-]*/gu :
            /[a-z0-9][a-z0-9'-]*/g;
        const matches = normalized.match(wordPattern);
        return matches ? matches : [];
    }

    buildIndex() {
        const getText = (el, sel) => {
            const n = el.querySelector(sel);
            return n ? n.textContent : '';
        };

        this.faqSearchIndex = this.faqItems.map((itemEl, idx) => {
            const questionText = getText(itemEl, '.accordion-title');
            const answerText = getText(itemEl, '.accordion-content__inner');
            const categoryText = itemEl.dataset.category;

            return {
                idx,
                itemEl,
                groupEl: itemEl.closest('[data-faq-group]'),
                questionText,
                answerText,
                categoryText,
                categoryRaw: categoryText,
                questionNorm: this.normalize(questionText),
                answerNorm: this.normalize(answerText),
                categoryNorm: this.normalize(categoryText),
                questionCollapsed: this.collapseSpaces(questionText),
                answerCollapsed: this.collapseSpaces(answerText),
                categoryCollapsed: this.collapseSpaces(categoryText)
            };
        });
    }

    reindexDeferred() {
        clearTimeout(this._reindexDebounceId);
        this._reindexDebounceId = setTimeout(() => {
            this.categoryGroups = Array.from(this.querySelectorAll('[data-faq-group]'));
            this.faqItems = Array.from(this.querySelectorAll('[data-faq-item]'));
            this.buildIndex();
            this.initViewMoreButtons();
            this.filterAndRender();
        }, 50);
    }

    initViewMoreButtons() {
        this.categoryGroups.forEach((groupEl) => {
            let btn = groupEl.querySelector('[data-faq-view-more]');
            if (!btn) {
                const container = groupEl.querySelector('.accordion-container');
                if (!container) return;

                const wrap = document.createElement('div');
                wrap.className = 'faq-view-more-wrap';

                btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'faq-view-more';
                btn.setAttribute('data-faq-view-more', '');
                btn.textContent = 'View more';
                btn.addEventListener('click', () => this.showAllItemsInGroup(groupEl));

                wrap.appendChild(btn);
                container.insertAdjacentElement('afterend', wrap);
            } else {
                btn.onclick = () => this.showAllItemsInGroup(groupEl);
            }
        });
    }
    limitVisibleItemsInGroup(groupEl, limit) {
        const container = groupEl.querySelector('.accordion-container');
        if (!container) return;
        const visible = Array.from(container.querySelectorAll('[data-faq-item]'))
            .filter((k) => k.style.display !== 'none');
        visible.forEach((el, idx) => {
            el.style.display = idx < limit ? '' : 'none';
        });
    }
    showAllItemsInGroup(groupEl) {
        const container = groupEl.querySelector('.accordion-container');
        if (!container) return;
        Array.from(container.querySelectorAll('[data-faq-item]')).forEach((el) => {
            el.style.display = '';
        });
        const btn = groupEl.querySelector('[data-faq-view-more]');
        if (btn) btn.style.display = 'none';
    }
    hasMoreThanLimit(container, limit) {
        const total = container ? container.querySelectorAll('[data-faq-item]').length : 0;
        return total > limit;
    }

    bindEvents() {
        if (this.navContainer) this.navContainer.addEventListener('click', this.onNavClick);
        if (this.searchSubmitBtn) this.searchSubmitBtn.addEventListener('click', this.onSearchSubmit);
        if (this.searchInput) this.searchInput.addEventListener('keydown', this.onSearchKeydown);
    }
    unbindEvents() {
        if (this.navContainer) this.navContainer.removeEventListener('click', this.onNavClick);
        if (this.searchSubmitBtn) this.searchSubmitBtn.removeEventListener('click', this.onSearchSubmit);
        if (this.searchInput) this.searchInput.removeEventListener('keydown', this.onSearchKeydown);
    }

    onNavClick(e) {
        const btn = e.target.closest('.faq-nav-btn');
        if (!btn) return;

        this.navButtons.forEach((x) => {
            const active = x === btn;
            x.classList.toggle('is-active', active);
            x.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        this.filterAndRender();
    }

    onSearchSubmit() {
        this.submittedQuery = this.searchInput ? this.searchInput.value.trim() : '';
        this.filterAndRender();
    }

    onSearchKeydown(e) {
        if (e.key === 'Enter') {
            this.submittedQuery = this.searchInput.value.trim();
            this.filterAndRender();
        } else if (e.key === 'Escape') {
            this.searchInput.value = '';
            this.submittedQuery = '';
            this.filterAndRender();
            this.searchInput.blur();
        }
    }

    filterAndRender() {
        const activeBtn = this.navContainer?.querySelector('.faq-nav-btn.is-active');
        let activeCat = activeBtn ? activeBtn.getAttribute('data-cat') : 'all';

        const rawQuery = this.submittedQuery || '';
        const phrase = this.collapseSpaces(rawQuery);
        const tokens = this.tokenize(rawQuery);
        const isSearching = rawQuery.length > 0;

        // If search should span all categories — auto-switch nav to "All FAQs"
        if (isSearching && this.searchScope === 'always_all' && activeCat !== 'all') {
            this.navButtons.forEach((b) => {
                const isAll = b.getAttribute('data-cat') === 'all';
                b.classList.toggle('is-active', isAll);
                b.setAttribute('aria-selected', isAll ? 'true' : 'false');
            });
            activeCat = 'all';
        }

        const restrictToActiveCat = (activeCat !== 'all') && (this.searchScope === 'only_active');

        let totalShown = 0;
        const visibleGroupMap = new Map();

        // Per-item match
        this.faqSearchIndex.forEach((rec) => {
            if (restrictToActiveCat && rec.categoryRaw !== activeCat) {
                this.setHidden(rec.itemEl);
                return;
            }

            if (!isSearching) {
                const inActiveScope = (activeCat === 'all') || (rec.categoryRaw === activeCat);
                inActiveScope ? this.setVisible(rec.itemEl) : this.setHidden(rec.itemEl);
                if (inActiveScope) {
                    totalShown++;
                    visibleGroupMap.set(rec.groupEl, true);
                }
                return;
            }

            let score = 0;
            const phraseHitQ = phrase && rec.questionCollapsed.includes(phrase);
            const phraseHitA = phrase && rec.answerCollapsed.includes(phrase);
            const phraseHitC = phrase && rec.categoryCollapsed.includes(phrase);
            if (phraseHitQ) score += 6;
            if (phraseHitA) score += 5;
            if (phraseHitC) score += 3;

            let tokenHits = 0;
            let allHit = true;
            for (const t of tokens) {
                const hit = rec.questionNorm.includes(t) || rec.answerNorm.includes(t) || rec.categoryNorm.includes(t);
                if (hit) tokenHits += 1;
                else allHit = false;
            }

            if (this.searchMode === 'phrase') {
                if (!(phrase && (phraseHitQ || phraseHitA || phraseHitC))) score = 0;
            } else if (this.searchMode === 'all') {
                if (!(tokens.length && allHit)) score = 0;
                else score += 2;
            } else if (this.searchMode === 'any') {
                if (tokenHits > 0) score += tokenHits;
                else score = 0;
            } else { // smart
                if (tokenHits > 0) score += tokenHits;
                if (tokens.length && allHit) score += 2;
            }

            if (score > 0) {
                this.setVisible(rec.itemEl);
                totalShown++;
                visibleGroupMap.set(rec.groupEl, true);
            } else {
                this.setHidden(rec.itemEl);
            }
        });

        // Per-group visibility + "View more"
        this.categoryGroups.forEach((groupEl) => {
            const hasVisibleItems = visibleGroupMap.get(groupEl) === true;
            const cat = groupEl.getAttribute('data-category');
            const container = groupEl.querySelector('.accordion-container');
            const viewMoreBtn = groupEl.querySelector('[data-faq-view-more]');

            if (!isSearching) {
                const shouldShowGroup = (activeCat === 'all') || (cat === activeCat);
                groupEl.style.display = shouldShowGroup ? '' : 'none';
                if (!shouldShowGroup) {
                    if (viewMoreBtn) viewMoreBtn.style.display = 'none';
                    return;
                }
                if (activeCat === 'all') {
                    this.limitVisibleItemsInGroup(groupEl, this.initialLimit);
                    if (viewMoreBtn) {
                        viewMoreBtn.style.display = this.hasMoreThanLimit(container, this.initialLimit) ? 'flex' : 'none';
                    }
                } else {
                    // A specific category is active: show every matching FAQ, no Load More needed.
                    Array.from(container.querySelectorAll('[data-faq-item]')).forEach((el) => {
                        el.style.display = '';
                    });
                    if (viewMoreBtn) viewMoreBtn.style.display = 'none';
                }
            } else {
                groupEl.style.display = hasVisibleItems ? '' : 'none';
                if (viewMoreBtn) viewMoreBtn.style.display = 'none';
            }
        });
        this.updateNoResultVisibility(totalShown);
        this.updateCountDisplay(totalShown);
    }

    updateNoResultVisibility(count) {
        if (!this.noResultEl) return;
        this.noResultEl.style.display = count === 0 ? '' : 'none';
    }

    updateCountDisplay(count) {
        if (!this.countEl) return;
        const text = count === 1 ? `${count} article` : `${count} articles`;
        this.countEl.textContent = text;
    }

    setHidden(el) {
        el.style.display = 'none';
    }
    setVisible(el) {
        el.style.display = '';
    }
}

if (!customElements.get('faq-section')) {
    customElements.define('faq-section', FaqSection);
}